import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "@/lib/api";
import { trackMarketingEvent } from "@/lib/marketing";

export default function AgentClaim() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("");
  const [agentId, setAgentId] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("No claim token provided.");
      return;
    }

    const claim = async () => {
      try {
        const { data } = await api.post("/agent-signups/claim", { claimToken: token });
        setStatus("success");
        setMessage(`Agent "${data.id}" claimed successfully!`);
        setAgentId(data.id);
        trackMarketingEvent("agent_claimed", { agent_id: data.id });
      } catch (err: unknown) {
        setStatus("error");
        const msg = (err as { response?: { data?: { message?: string } } })
          ?.response?.data?.message || "Failed to claim agent. The token may be invalid or expired.";
        setMessage(msg);
      }
    };

    claim();
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {status === "loading" && <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary" />}
          {status === "success" && <CheckCircle2 className="mx-auto h-12 w-12 text-green-500" />}
          {status === "error" && <XCircle className="mx-auto h-12 w-12 text-destructive" />}
          <CardTitle className="mt-4">
            {status === "loading" && "Claiming Agent..."}
            {status === "success" && "Agent Claimed!"}
            {status === "error" && "Claim Failed"}
          </CardTitle>
          <CardDescription>{message}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {status === "success" && agentId && (
            <Button className="w-full" onClick={() => navigate(`/agents/${agentId}`)}>
              View Agent
            </Button>
          )}
          <Button variant="outline" className="w-full" onClick={() => navigate("/dashboard")}>
            Go to Dashboard
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
